export type CsvBrokerOption = { id: string; name: string };

export const brokerOptions: CsvBrokerOption[] = [
  { id: 'interactive_brokers', name: 'Interactive Brokers' },
  { id: 'tradestation', name: 'TradeStation' },
  { id: 'ninjatrader', name: 'NinjaTrader' },
  { id: 'thinkorswim', name: 'Thinkorswim' },
  { id: 'td_ameritrade', name: 'TD Ameritrade' },
  { id: 'charles_schwab', name: 'Charles Schwab' },
  { id: 'etrade', name: 'E*TRADE' },
  { id: 'fidelity', name: 'Fidelity Investments' },
  { id: 'webull', name: 'Webull' },
  { id: 'robinhood', name: 'Robinhood' },
  { id: 'alpaca', name: 'Alpaca' },
  { id: 'questrade', name: 'Questrade' },
  { id: 'wealthsimple_trade', name: 'Wealthsimple Trade' },
  { id: 'national_bank_direct_brokerage', name: 'National Bank Direct Brokerage (NBDB)' },
  { id: 'rbc_direct_investing', name: 'RBC Direct Investing' },
  { id: 'cibc_investors_edge', name: 'CIBC Investor’s Edge' },
  { id: 'scotia_itrade', name: 'Scotia iTRADE' },
  { id: 'bmo_investorline', name: 'BMO InvestorLine' },
  { id: 'degiro', name: 'DEGIRO' },
  { id: 'trading_212', name: 'Trading 212' },
  { id: 'zerodha', name: 'Zerodha' },
  { id: 'tastytrade', name: 'tastytrade' },
  { id: 'tradovate', name: 'Tradovate' },
  { id: 'amp_futures', name: 'AMP Futures' },
  { id: 'optimus_futures', name: 'Optimus Futures' },
  { id: 'edge_clear', name: 'Edge Clear' },
  { id: 'ironbeam', name: 'Ironbeam' },
  { id: 'rithmic', name: 'Rithmic' },
  { id: 'cqg', name: 'CQG' },
  { id: 'sierra_chart', name: 'Sierra Chart' },
  { id: 'quantower', name: 'Quantower' },
  { id: 'mt4', name: 'MetaTrader 4' },
  { id: 'mt5', name: 'MetaTrader 5' },
  { id: 'tradingview', name: 'TradingView' },
  { id: 'warrior_trading_simulator', name: 'Warrior Trading Simulator' },
  { id: 'binance', name: 'Binance' },
  { id: 'coinbase', name: 'Coinbase' },
  { id: 'kraken', name: 'Kraken' },
  { id: 'bybit', name: 'Bybit' },
  { id: 'okx', name: 'OKX' },
  { id: 'kucoin', name: 'KuCoin' },
  { id: 'gemini', name: 'Gemini' },
  // Explicit user-selected source. `csv_generic` remains the internal unknown-identity sentinel.
  { id: 'generic_csv', name: 'CSV / Generic Export' },
].sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));

export type CsvBrokerDetection =
  | { status: 'detected'; brokerId: string }
  | { status: 'not_detected' }
  | { status: 'ambiguous'; reason: 'unsupported' | 'multiple' };

function normalizeBrokerIdentity(value: unknown): string | null {
  const identity = String(value ?? '').trim().toLocaleLowerCase('en-US');
  if (!identity || identity === 'auto' || identity === 'csv_generic' || identity === 'generic_csv') return null;
  const aliases: Record<string, string> = {
    ibkr: 'interactive_brokers', schwab: 'charles_schwab', fidelity: 'fidelity',
    nbdb: 'national_bank_direct_brokerage',
    'national bank direct brokerage': 'national_bank_direct_brokerage',
    wealthsimple: 'wealthsimple_trade',
    'cibc investor\'s edge': 'cibc_investors_edge',
  };
  if (aliases[identity]) return aliases[identity];
  const option = brokerOptions.find((candidate) =>
    candidate.id === identity || candidate.name.toLocaleLowerCase('en-US') === identity,
  );
  return option && option.id !== 'generic_csv' ? option.id : null;
}

/** Detect only explicit, consistent broker identities carried by CSV rows/trades. */
export function detectCsvBroker(rows: unknown[], trades: unknown[] = []): CsvBrokerDetection {
  const records = [...(Array.isArray(rows) ? rows : []), ...(Array.isArray(trades) ? trades : [])];
  const rawIdentities = records.flatMap((record) => {
    if (!record || typeof record !== 'object') return [];
    const fields = Object.entries(record as Record<string, unknown>);
    const value = fields.find(([key]) => key.trim().toLowerCase().replace(/[^a-z0-9]/g, '')
      .match(/^(sourcebroker|broker|brokerid)$/))?.[1];
    return value == null || !String(value).trim() ? [] : [String(value).trim()];
  });
  if (rawIdentities.length === 0) return { status: 'not_detected' };
  const brokerIds = rawIdentities.map(normalizeBrokerIdentity);
  if (brokerIds.some((id) => !id)) return { status: 'ambiguous', reason: 'unsupported' };
  const distinct = [...new Set(brokerIds as string[])];
  return distinct.length === 1
    ? { status: 'detected', brokerId: distinct[0] }
    : { status: 'ambiguous', reason: 'multiple' };
}
